import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

/**
 * Blade 1: catalog of available groupings. Click an item to add it to the
 * Selected blade. Items already in Selected are dimmed and non-clickable.
 *
 * Renders one section per category supplied by `useGroupingCatalog().sections`.
 * Section ordering and intra-section sort are pre-computed by the hook; this
 * component is a pure renderer. While the firm-specific SC methods are still
 * loading, a small footer hint is rendered -- SC entries will flow into their
 * semantic category (e.g. Asset) once they arrive.
 */
function CatalogItem ({ opt, onAdd, classes }) {
  return (
    <div
      className={clsx(classes.catalogItem, { [classes.catalogItemAdded]: opt.alreadyAdded })}
      onClick={opt.alreadyAdded ? undefined : () => onAdd(opt.value)}
      role='button'
      tabIndex={opt.alreadyAdded ? -1 : 0}
      aria-disabled={opt.alreadyAdded ? 'true' : undefined}
      onKeyDown={(e) => {
        if (opt.alreadyAdded) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onAdd(opt.value)
        }
      }}
    >
      {opt.label}
    </div>
  )
}

CatalogItem.propTypes = {
  opt: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    alreadyAdded: PropTypes.bool
  }).isRequired,
  onAdd: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

export default function GroupingCatalogPanel ({ sections, isLoadingDynamic, onAdd, classes }) {
  // The divider sits between the last 'primary' section and the first
  // 'secondary' section -- per CEO direction, account/registration/household
  // are visually separated from the rest. Render only when both tiers are
  // present, so all-primary or all-secondary catalogs don't get an orphan.
  const firstSecondaryIdx = sections.findIndex(s => s.tier === 'secondary')
  const hasPrimary = sections.some(s => s.tier === 'primary')
  const showDivider = hasPrimary && firstSecondaryIdx > 0

  return (
    <div className={classes.catalogPanel}>
      <div className={classes.panelHeader}>Available Groupings</div>
      <div className={classes.catalogList}>
        {sections.map(({ category, options }, idx) => (
          <React.Fragment key={category}>
            {showDivider && idx === firstSecondaryIdx && (
              <div className={classes.catalogDivider} role='separator' aria-hidden='true' />
            )}
            <div className={classes.catalogSection}>
              <div className={classes.catalogSectionHeader}>{category} Groupings</div>
              {options.map(opt => (
                <CatalogItem key={opt.value} opt={opt} onAdd={onAdd} classes={classes} />
              ))}
            </div>
          </React.Fragment>
        ))}
        {isLoadingDynamic && (
          <div className={classes.catalogFooterHint}>Loading firm-specific classifications…</div>
        )}
      </div>
    </div>
  )
}

GroupingCatalogPanel.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string.isRequired,
    tier: PropTypes.oneOf(['primary', 'secondary']).isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      alreadyAdded: PropTypes.bool
    })).isRequired
  })).isRequired,
  isLoadingDynamic: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}
